// src/com/user_info/UserInfoListForSms.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import {useLazyQuery, useReactiveVar} from '@apollo/client';
import {userInfoListRepo} from '../../model/CvoModel';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import FlexyTable from '../common/flexytable/FlexyTable';
import { AppObject, AppPalette, AppWord, TableColumnType } from '../../model/AppConst';
import styled from '@emotion/styled';
import { LIST_USER_INFO } from './UserInfoGql';
import ResponseAlert from '../message/ResponseAlert';
import SearchBox from '../common/SearchBox';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import { useSnackbar } from 'notistack';

const MainListBox = styled(Box)({height:'100%', display:'flex', flexDirection:'column'});

const TableColumns = [
	{ id: 'userId', label: '아이디', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'userName', label: '이름', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'mobile', label: '휴대전화', width: 110, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'telno', label: '연락처', width: 110, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'userStateValue', label: '상태', width: 80, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'className', label: '권한', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'loginTime', label: '로그인', width: 180, show: true, option:{align: 'inherit', type: TableColumnType.DATETIME} },
    { id: 'email', label: '메일', width: 200, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
];

const MinSecReloadInterval = 5000;

export default function UserInfoListForSms({
    onRequestAddToReceiver,
    onRequestAddAllToReceiver
}) {
    const userInfoList = useReactiveVar(userInfoListRepo);
    const [search, setSearch] = useState('');
    const [responseAlert, setResponseAlert] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const loadedRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getUserInfoList, responseList] = useLazyQuery(LIST_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: "사용자 목록 조회 오류"})}
    });


    useEffect(() => {
        getUserInfoList();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.userInfoList) {
            loadedRef.current = (new Date()).getTime();
            userInfoListRepo(data.userInfoList);
        }
    };

    const onChangeSearch = (text) => {
        setSearch(text);
    };


    const onRequestReload = () => {
        const diff = (new Date()).getTime() - loadedRef.current;
        if(diff > MinSecReloadInterval) {
            getUserInfoList();
        }
        else {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };

    const onClickAddAll = () => {
        const all = userInfoList.filter(v => v.userStateCd==='NORMAL' && v.mobile && v.mobile.length >= 10).map(v => ({tel: v.mobile, name: v.userName}));
        if(all.length > 0) onRequestAddAllToReceiver(all);
    };

    const onClickAdd = (row) => {
        if(row.mobile && row.mobile.length >= 10) {
            onRequestAddToReceiver({tel: row.mobile, name: row.userName});
        }
        else {
            enqueueSnackbar(row.userName + '님의 휴대전화번호가 올바르지 않습니다.',
                {variant:AppPalette.ErrorColor, autoHideDuration:1500});
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppWord.CMD_ADD: onClickAdd(row); break;
            default: return;
        }
    };

    const Tools = [
        {id:AppWord.CMD_ADD, icon:<MobileFriendlyIcon fontSize='small' />},
    ];

    return(
        <MainListBox>
            <ContentHeader>
                <HeaderLeft>
                    <SearchBox onChange={onChangeSearch} value={search}/>
                </HeaderLeft>
                <HeaderRight>
                    <Button
                        startIcon={<PostAddIcon />}
                        variant='contained'
                        onClick={onClickAddAll}
                        color={AppPalette.InheritColor}
                    >
                        모두 추가
                    </Button>
                </HeaderRight>
            </ContentHeader>
            <FlexyTable
                name="user_info_list_for_sms"
                uniqueKey="userId"
                columns={TableColumns}
                records={userInfoList}
                search={search}
                tools={Tools}
                onClickTool={onClickToolOnRow}
                onRequestReload={onRequestReload}
                onClickOneRow={onClickAdd}
                defaultSorter={{id:'userName', type: TableColumnType.TEXT}}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </MainListBox>
    );

}
